import type { ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'
import { prefersReducedMotion } from '@/lib/preferences'

interface StarDriftProps {
    className?: string
    density?: number
    speed?: number
}

type DriftStar = {
    x: number
    y: number
    depth: number
    radius: number
    phase: number
    warmth: number
}

/** A slowly drifting field of stars on a 2D canvas. Pauses when off screen. */
export const StarDrift = ({ className, density = 0.00022, speed = 1 }: StarDriftProps): ReactNode => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null)

    useEffect(() => {
        const canvas = canvasRef.current
        const context = canvas?.getContext('2d')

        if (!canvas || !context) return

        let width = 0
        let height = 0
        let stars: DriftStar[] = []
        let frame = 0
        let last = performance.now()
        let visible = true

        const seed = () => {
            const count = Math.max(60, Math.round(width * height * density))

            stars = Array.from({ length: count }, () => {
                const depth = Math.random()

                return {
                    x: Math.random() * width,
                    y: Math.random() * height,
                    depth,
                    radius: 0.35 + Math.pow(Math.random(), 6) * 1.5 + depth * 0.25,
                    phase: Math.random() * Math.PI * 2,
                    warmth: Math.random()
                }
            })
        }

        const resize = () => {
            const ratio = Math.min(window.devicePixelRatio || 1, 2)

            width = canvas.clientWidth
            height = canvas.clientHeight
            canvas.width = Math.round(width * ratio)
            canvas.height = Math.round(height * ratio)
            context.setTransform(ratio, 0, 0, ratio, 0, 0)
            seed()
        }

        const draw = (now: number) => {
            const delta = Math.min(64, now - last) / 1000
            const still = prefersReducedMotion()

            last = now
            context.clearRect(0, 0, width, height)

            for (const star of stars) {
                if (!still) {
                    star.x += (2 + star.depth * 7) * speed * delta
                    star.y -= (0.6 + star.depth * 1.6) * speed * delta
                    if (star.x > width + 2) star.x = -2
                    if (star.y < -2) star.y = height + 2
                }

                const twinkle = still ? 1 : 0.72 + 0.28 * Math.sin(now * 0.0011 + star.phase)
                const alpha = (0.25 + star.depth * 0.6) * twinkle
                const red = 236 + Math.round(star.warmth * 19)
                const green = 228 + Math.round(star.warmth * 8)
                const blue = 214 + Math.round((1 - star.warmth) * 30)

                context.fillStyle = `rgba(${red}, ${green}, ${blue}, ${alpha})`
                context.beginPath()
                context.arc(star.x, star.y, star.radius, 0, Math.PI * 2)
                context.fill()
            }

            if (visible) frame = requestAnimationFrame(draw)
        }

        const observer = new IntersectionObserver(([entry]) => {
            const wasVisible = visible

            visible = entry.isIntersecting
            if (visible && !wasVisible) {
                cancelAnimationFrame(frame)
                last = performance.now()
                frame = requestAnimationFrame(draw)
            }
        })

        resize()
        observer.observe(canvas)
        frame = requestAnimationFrame(draw)
        window.addEventListener('resize', resize)

        return () => {
            cancelAnimationFrame(frame)
            observer.disconnect()
            window.removeEventListener('resize', resize)
        }
    }, [density, speed])

    return (
        <div aria-hidden className={cn('media pointer-events-none', className)}>
            <canvas ref={canvasRef} className='block h-full w-full' />
        </div>
    )
}
